import styled from "styled-components";
import GridContainer from "../grid/GridContainer";
import GridColumn from "../grid/GridColumn";
import RitualButton from "../global/RitualButton";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import EditorialBlockVideo from "../plp/EditorialBlockVideo";
import { VideoContent } from "../templates/homepage.new";
import stripPTags from "../../utils/stripPTag";

const HomepageHeroWrapper = styled.div<{ offset: number }>`
  width: 100%;
  position: relative;
  margin-bottom: var(--spacing-5);

  @media (min-width: 750px) {
    height: 400px;
  }

  @media (min-width: 933px) {
    height: calc(100svh - 52px - ${({ offset }) => offset}px);
    max-height: 600px;
  }

  @media (min-width: 960px) {
    height: calc(100svh - 64px - ${({ offset }) => offset}px);
  }

  .r-grid-container {
    height: 100%;
  }
`;

const HeroRow = styled.div.attrs({
  className: "r-grid-row",
})`
  row-gap: var(--spacing-2);
`;

const MobileContent = styled.div`
  width: 100vw;
  aspect-ratio: 375 / 259;
  margin-left: -24px;
  overflow: hidden;

  @media (min-width: 750px) {
    display: none;
  }
`;

const MediaContainer = styled.div`
  background-color: #ccc;
  position: absolute;
  left: 0;
  top: 0;
  height: 53svh;
  width: 100%;
  display: none;

  .desktop-image {
    display: none;
  }

  @media (min-width: 750px) {
    display: block;
    height: 100%;
    width: 55%;

    .desktop-image {
      display: block;
    }
  }
  @media (min-width: 933px) {
    display: block;
    width: 63%;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0 0 var(--spacing-3);
  height: 100%;

  text-align: center;

  p {
    margin: 0;
  }

  h1 {
    margin-bottom: var(--spacing-0_75);

    em {
      font-family: var(--font-dutch);
    }
  }

  > div {
    > p {
      margin-bottom: var(--spacing-1_5);
    }
  }

  @media (min-width: 750px) {
    padding: var(--spacing-1_5) 0;
    text-align: left;

    h1 {
      margin-bottom: var(--spacing-1);
    }
  }

  @media (min-width: 933px) {
    padding: var(--spacing-3) 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--spacing-0_75);
  flex-wrap: wrap;

  @media (min-width: 750px) {
    justify-content: flex-start;
  }
`;

type HomepageHeroProps = {
  offset: number;
  headline?: {
    childMarkdownRemark: {
      html: string;
    };
  };
  subhead?: {
    childMarkdownRemark: {
      html: string;
    };
  };
  links?: {
    text: string;
    to: {
      path?: string;
      slug?: string;
    };
  }[];
  textColor?: string;
  backgroundColor?: string;
  image?: {
    gatsbyImageData: IGatsbyImageData;
    description: string;
  };
  imageMobile?: {
    gatsbyImageData: IGatsbyImageData;
    description: string;
  };
  video?: VideoContent;
  videoMobile?: VideoContent;
};
const HomepageHeroLeft = ({
  offset,
  headline,
  subhead,
  links,
  textColor,
  backgroundColor,
  image,
  imageMobile,
  video,
  videoMobile,
}: HomepageHeroProps) => {
  return (
    <HomepageHeroWrapper
      offset={offset}
      style={{ backgroundColor }}
      data-testid="homepage-hero-wrapper"
    >
      <MediaContainer>
        {!video && image && (
          <GatsbyImage
            image={image?.gatsbyImageData}
            alt={image?.description || ""}
            className="desktop-image"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        )}
        {video && (
          <EditorialBlockVideo
            item={{
              content: {
                videoContent: video,
              },
              size: "small",
            }}
          />
        )}
      </MediaContainer>
      <GridContainer>
        <HeroRow>
          <GridColumn
            xs={{
              size: 6,
            }}
            s={{
              size: 7,
            }}
            m={{
              size: 8,
            }}
          >
            <MobileContent>
              {!videoMobile && imageMobile && (
                <GatsbyImage
                  image={imageMobile?.gatsbyImageData}
                  alt={imageMobile?.description || ""}
                  className="mobile-image"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              )}
              {videoMobile && (
                <EditorialBlockVideo
                  item={{
                    content: {
                      videoContent: videoMobile,
                    },
                    size: "small",
                  }}
                />
              )}
            </MobileContent>
          </GridColumn>
          <GridColumn
            xs={{
              size: 6,
            }}
            s={{
              size: 5,
            }}
            m={{
              size: 4,
            }}
          >
            <Content
              style={{ color: textColor }}
              data-testid="homepage-hero-content"
            >
              <div>
                <h1
                  className="typography-headline1"
                  dangerouslySetInnerHTML={{
                    __html: stripPTags(
                      headline?.childMarkdownRemark.html || "",
                    ),
                  }}
                />
                <p
                  className="typography-body1 font-circular"
                  dangerouslySetInnerHTML={{
                    __html: stripPTags(subhead?.childMarkdownRemark.html || ""),
                  }}
                />
              </div>
              <ButtonContainer>
                {links?.map((link) => (
                  <RitualButton
                    className="short"
                    to={link.to?.path || link.to?.slug || ""}
                  >
                    {link.text}
                  </RitualButton>
                ))}
              </ButtonContainer>
            </Content>
          </GridColumn>
        </HeroRow>
      </GridContainer>
    </HomepageHeroWrapper>
  );
};

export default HomepageHeroLeft;
